import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { API } from './api';
import type { Job, Page } from './types';

export type ConnectionState = 'connecting' | 'open' | 'paused' | 'closed';

const RealtimeContext = createContext<ConnectionState>('connecting');

/** Tiempo en segundo plano antes de soltar la conexión. */
const HIDDEN_GRACE_MS = 30_000;

let refetchTimer: ReturnType<typeof setTimeout> | undefined;
/** Agrupa recargas: un trabajo fuera de la página visible emite muchos eventos de progreso. */
function refetchListsSoon(qc: QueryClient) {
  clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => void qc.invalidateQueries({ queryKey: ['jobs'] }), 800);
}

/** Aplica un trabajo recibido por el canal a la caché de listas y detalle. */
function applyJob(qc: QueryClient, job: Job) {
  qc.setQueryData<Job>(['job', job.id], job);
  let found = false;
  qc.setQueriesData<Page<Job>>({ queryKey: ['jobs'] }, (page) => {
    if (!page) return page;
    const idx = page.items.findIndex((j) => j.id === job.id);
    if (idx < 0) return page;
    found = true;
    const items = page.items.slice();
    items[idx] = job;
    return { ...page, items };
  });
  // Trabajo nuevo: recargar las listas para colocarlo según filtros y orden.
  if (!found) refetchListsSoon(qc);
  // Consumo y cuotas cambian al terminar.
  if (job.status === 'done') void qc.invalidateQueries({ queryKey: ['usage'] });
}

/**
 * Canal de eventos del servidor. Con HTTP/1.1 el navegador solo mantiene 6 conexiones por
 * servidor y cada EventSource ocupa una, así que la conexión se suelta al salir de la página
 * o cuando la pestaña lleva un rato oculta, y se recupera (refrescando datos) al volver.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = useState<ConnectionState>('connecting');

  useEffect(() => {
    let es: EventSource | null = null;
    let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
    let everOpened = false;

    const connect = () => {
      if (es) return;
      setState('connecting');
      const source = new EventSource(`${API}/events`, { withCredentials: true });
      es = source;
      source.addEventListener('ready', () => {
        setState('open');
        // Tras una reconexión se pudo perder algún evento.
        if (everOpened) void qc.invalidateQueries({ queryKey: ['jobs'] });
        everOpened = true;
      });
      source.addEventListener('job', (e) => applyJob(qc, JSON.parse((e as MessageEvent).data)));
      source.addEventListener('unauthorized', () => {
        disconnect('closed');
        qc.setQueryData(['me'], null);
      });
      source.onerror = () => setState(source.readyState === EventSource.CLOSED ? 'closed' : 'connecting');
    };

    const disconnect = (next: ConnectionState) => {
      es?.close();
      es = null;
      setState(next);
    };

    const onVisibility = () => {
      clearTimeout(hiddenTimer);
      if (document.visibilityState === 'hidden') {
        hiddenTimer = setTimeout(() => disconnect('paused'), HIDDEN_GRACE_MS);
      } else {
        connect();
      }
    };
    const onPageHide = () => disconnect('paused');
    const onPageShow = (e: PageTransitionEvent) => e.persisted && connect();

    connect();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      clearTimeout(hiddenTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      es?.close();
    };
  }, [qc]);

  return <RealtimeContext.Provider value={state}>{children}</RealtimeContext.Provider>;
}

export const useConnection = () => useContext(RealtimeContext);
